import {
  completeQueryResult,
  throwOnQueryError,
} from 'ZalamoLibrary/src/lib/core/graphql/complete-query-data';

import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { OrganizationService } from 'ZalamoLibrary/src/lib/core/organizations/organization.service';
import {
  GetPhotographerPublicStateGQL,
  PhotographerPublicStateField,
  SetPhotographerPublicStateGQL,
} from '../../../graphql';
import {
  BehaviorSubject,
  Subject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  finalize,
  first,
  map,
  of,
  shareReplay,
  switchMap,
  takeUntil,
  timer,
} from 'rxjs';

interface FiscalMachineState {
  organizationId: string;
  loading: boolean;
  value: boolean | null;
  failed: boolean;
}

interface FiscalMachineQuestionViewModel {
  organizationId: string;
  showOverlay: boolean;
  showQuestion: boolean;
  showThanks: boolean;
  isSaving: boolean;
  hasError: boolean;
}

const FISCAL_MACHINE_STATE_FIELD =
  PhotographerPublicStateField.HasFiscalMachine;

@Component({
  selector: 'app-fiscal-machine-state-question',
  templateUrl: './fiscal-machine-state-question.component.html',
  styleUrls: ['./fiscal-machine-state-question.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FiscalMachineStateQuestionComponent implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly savingOrganizationId$ = new BehaviorSubject<string | null>(
    null,
  );
  private readonly thankYouOrganizationId$ = new BehaviorSubject<string | null>(
    null,
  );
  private readonly errorOrganizationId$ = new BehaviorSubject<string | null>(
    null,
  );

  private readonly currentOrganizationId$ = this.organizationService
    .getCurrentOrganization()
    .pipe(
      filter((organizationId): organizationId is string => !!organizationId),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

  private readonly fiscalMachineState$ = this.currentOrganizationId$.pipe(
    switchMap((organizationId) =>
      this.getPhotographerPublicStateGQL
        .watch({
          variables: {
            organizationId,
            field: FISCAL_MACHINE_STATE_FIELD,
          },
        })
        .valueChanges.pipe(
          throwOnQueryError,
          map((apolloQueryResult): FiscalMachineState => {
            const { data, loading } = completeQueryResult(apolloQueryResult);
            return {
              organizationId,
              loading,
              value: data?.photographerPublicState?.booleanValue ?? null,
              failed: false,
            };
          }),
          catchError(() =>
            of({
              organizationId,
              loading: false,
              value: null,
              failed: true,
            }),
          ),
        ),
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly vm$ = combineLatest([
    this.fiscalMachineState$,
    this.savingOrganizationId$,
    this.thankYouOrganizationId$,
    this.errorOrganizationId$,
  ]).pipe(
    map(
      ([
        state,
        savingOrganizationId,
        thankYouOrganizationId,
        errorOrganizationId,
      ]): FiscalMachineQuestionViewModel => {
        const isSaving = savingOrganizationId === state.organizationId;
        const showThanks = thankYouOrganizationId === state.organizationId;
        const shouldAsk =
          !state.loading && !state.failed && state.value === null;

        return {
          organizationId: state.organizationId,
          showOverlay: showThanks || isSaving || shouldAsk,
          showQuestion: !showThanks,
          showThanks,
          isSaving,
          hasError: errorOrganizationId === state.organizationId,
        };
      },
    ),
  );

  constructor(
    private readonly organizationService: OrganizationService,
    private readonly getPhotographerPublicStateGQL: GetPhotographerPublicStateGQL,
    private readonly setPhotographerPublicStateGQL: SetPhotographerPublicStateGQL,
  ) {}

  answerYes(organizationId: string): void {
    this.answer(organizationId, true);
  }

  answerNo(organizationId: string): void {
    this.answer(organizationId, false);
  }

  ngOnDestroy(): void {
    this.destroy$.next(undefined);
    this.destroy$.complete();
  }

  private answer(organizationId: string, booleanValue: boolean): void {
    if (
      !organizationId ||
      this.savingOrganizationId$.value === organizationId
    ) {
      return;
    }

    this.errorOrganizationId$.next(null);
    this.savingOrganizationId$.next(organizationId);
    this.setPhotographerPublicStateGQL
      .mutate({
        ...{
          refetchQueries: [
            {
              query: this.getPhotographerPublicStateGQL.document,
              variables: {
                organizationId,
                field: FISCAL_MACHINE_STATE_FIELD,
              },
            },
          ],
        },
        variables: {
          organizationId,
          field: FISCAL_MACHINE_STATE_FIELD,
          booleanValue,
        },
      })
      .pipe(
        first(),
        finalize(() => this.savingOrganizationId$.next(null)),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: () => this.showThankYou(organizationId),
        error: () => this.errorOrganizationId$.next(organizationId),
      });
  }

  private showThankYou(organizationId: string): void {
    this.thankYouOrganizationId$.next(organizationId);
    timer(3000)
      .pipe(first(), takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.thankYouOrganizationId$.value === organizationId) {
          this.thankYouOrganizationId$.next(null);
        }
      });
  }
}
